import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import SuperAdminPage from './pages/SuperAdminPage';
import VerifierPage from './pages/VerifierPage';
import CollectorPage from './pages/CollectorPage';
import PaymentRedeemerPage from './pages/PaymentRedeemerPage';
import PaymentPosterPage from './pages/PaymentPosterPage';
import HearingRepresentativePage from './pages/HearingRepresentativePage';
import ProviderPage from './pages/ProviderPage';
// A wrapper for routes that should only be accessible to logged-out users
function LoggedInRedirect({ children }) {
  const { isAuthenticated, user, loading }
= useAuth();

  // Wait until authentication status is confirmed
  if (loading) {
    return <div>Loading...</div>; // Or a spinner component
  }

  if (isAuthenticated && user) {
    // If the user is authenticated, redirect them to their specific role-based page
    const { role } = user;
    let targetPath = '/dashboard'; // Default fallback
    switch (role) {
      case 'Super Admin':
        targetPath = '/superadmin';
        break;
      case 'Admin':
        targetPath = '/admin';
        break;
      case 'Verifier':
        targetPath = '/verifier';
        break;
      case 'Collector':
        targetPath = '/collector';
        break;
      case 'Payment Redeemer':
        targetPath = '/payment-redeemer';
        break;
      case 'Payment Poster':
        targetPath = '/payment-poster';
        break;
      case 'Hearing Representative':
        targetPath = '/hearing-representative';
        break;  
      case 'Provider':
        targetPath = '/provider';
        break;
      default:
        targetPath = '/dashboard';
    }
    return <Navigate to={targetPath} replace />;
  }

  return children;
}


// A wrapper for routes that require simple authentication
function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div>Loading...</div>;
  }
  return isAuthenticated ? children : <Navigate to="/login" replace/>;
}

// A wrapper for routes that require a specific role
function RoleProtectedRoute({ children, allowedRoles }) {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        // If not authenticated, redirect to the login page
        return <Navigate to="/login" replace/>;
    }
    
    // Check if the user's role is in the allowed list
    const isAuthorized = user && allowedRoles.includes(user.role);

    // If authorized, render the component. Otherwise, redirect to a fallback page.
    return isAuthorized ? children : <Navigate to="/dashboard" replace/>;
}

// New component to redirect users from a generic dashboard to their role-specific page
function DashboardRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    // This should not be reached if wrapped in PrivateRoute, but as a safeguard:
    return <Navigate to="/login" replace />;
  }

  const { role } = user;
  // Redirect users to their specific page based on their role
  switch (role) {
    case 'Super Admin':
      return <Navigate to="/superadmin" replace />;
    case 'Admin':
      return <Navigate to="/admin" replace />;
    case 'Verifier':
      return <Navigate to="/verifier" replace />;
    case 'Collector':
      return <Navigate to="/collector" replace />;
    case 'Payment Redeemer':
      return <Navigate to="/payment-redeemer" replace />;
    case 'Payment Poster':
      return <Navigate to="/payment-poster" replace />;
    case 'Hearing Representative':
      return <Navigate to="/hearing-representative" replace />;
    case 'Provider':
      return <Navigate to="/provider" replace />;
    default:
      // If a user's role has no specific page, they can see the generic dashboard
      return <DashboardPage />;
  }
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="bg-gray-100 min-h-screen">
          <Routes>
            {/* Public Route: only accessible when logged out */}
            <Route 
              path="/login" 
              element={
                <LoggedInRedirect>
                  <LoginPage />
                </LoggedInRedirect>
              } 
            />
            
            {/* Generic Authenticated Route is now a redirector */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardRedirect />
                </PrivateRoute>
              }
            />

            {/* Role-Specific Protected Routes */}
            <Route
              path="/admin"
              element={
                <RoleProtectedRoute allowedRoles={['Admin']}>
                  <AdminPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/superadmin"
              element={
                <RoleProtectedRoute allowedRoles={['Super Admin']}>
                  <SuperAdminPage/>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/verifier"
              element={
                <RoleProtectedRoute allowedRoles={['Verifier']}>
                  <VerifierPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/collector"
              element={
                <RoleProtectedRoute allowedRoles={['Collector']}>
                  <CollectorPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/payment-redeemer"
              element={
                <RoleProtectedRoute allowedRoles={['Payment Redeemer']}>
                  <PaymentRedeemerPage />
                </RoleProtectedRoute>
              }
            />
             <Route
              path="/payment-poster"
              element={
                <RoleProtectedRoute allowedRoles={['Payment Poster']}>
                  <PaymentPosterPage />
                </RoleProtectedRoute>
              }
            />
            <Route
            path="/hearing-representative"
            element={
            <RoleProtectedRoute allowedRoles={['Hearing Representative']}>
            <HearingRepresentativePage />
            </RoleProtectedRoute>
            }
            />
            <Route
            path="/provider"
            element={
            <RoleProtectedRoute allowedRoles={['Provider']}>
            <ProviderPage />
            </RoleProtectedRoute>
            }
            />
            {/* Fallback route to redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace/>} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

